import React, { useEffect, useMemo, useState } from 'react';
import { Form } from 'react-final-form';
import { useIntl } from 'react-intl';

import { List, Pill, PillAction, Popper } from '@xsolla/ui-core';
import { Modal } from '@xsolla/ui-modal';

import { disconnectPlatform, setPlatformConnection, updatePlatformConnection } from '../../../api';
import { ConfigurableField } from '../../../api/configurations/social/types';
import { useConfig } from '../../../contexts/config';
import { usePlatformSettings } from '../../../contexts/settings';
import { useNotifications } from '../../../hooks/useNotifications';
import { FieldInput, FormFooter } from '../../ui';
import { PlatformFieldsWrapper, PlatformHeader, PlatformHeaderWrapper } from './style';
import { FormValues, platformSettingsValidator } from './validator';

type Props = {
  projectId: string;
  onClose: () => void;
  provider: string;
};

export const PlatformSettingsModal = ({ onClose, provider, projectId }: Props): JSX.Element => {
  const intl = useIntl();
  const { xsollaAnalytics } = useConfig();
  const [existData, setExistData] = useState<boolean>(false);
  const [fetching, setFetching] = useState<boolean>(false);
  const { platformSettings, setPlatformSettings } = usePlatformSettings();
  const { showErrorNotify, showSuccessNotify } = useNotifications();

  const currentPlatform = useMemo(
    () => platformSettings?.find((i) => i.provider === provider),
    [platformSettings]
  );

  const platformData = useMemo(() => {
    const [client_id, client_secret] = currentPlatform?.configurable_fields as ConfigurableField[];
    return {
      client_id: client_id?.value,
      client_secret: client_secret?.value
    };
  }, [currentPlatform]);

  useEffect(() => {
    if (platformData.client_id) {
      setExistData(true);
    }
    if (currentPlatform?.connected) {
      setExistData(true);
    }
  }, [platformData]);

  const onSubmit = async (values: FormValues) => {
    try {
      setFetching(true);
      if (existData) {
        await updatePlatformConnection({ projectId, provider, values });
      } else {
        await setPlatformConnection({ projectId, provider, values });
        setExistData(true);
      }
      setPlatformSettings((prev) =>
        prev.map((platform) => {
          if (platform.provider === provider) {
            const newConfigurableFields = platform.configurable_fields.map((field) => {
              if (Object.keys(values).includes(field.name)) {
                return {
                  ...field,
                  value: values[field.name]
                };
              }

              return field;
            });

            return {
              ...platform,
              configurable_fields: newConfigurableFields,
              connected: true
            };
          }
          return platform;
        })
      );

      xsollaAnalytics.sendClick(`login_pa_module__platform-settings__connect`, projectId, {
        social: provider,
        ...values
      });

      showSuccessNotify();
    } catch (err) {
      showErrorNotify(err);
    } finally {
      setFetching(false);
    }
  };

  const disconnectHandle = async () => {
    try {
      setFetching(true);
      await disconnectPlatform({ projectId, provider });

      setPlatformSettings((prev) =>
        prev.map((platform) => {
          const newConfigurableFields = platform.configurable_fields.map((field) => {
            return { ...field, value: null };
          });

          if (platform.provider === provider) {
            return {
              ...platform,
              connected: false,
              configurable_fields: newConfigurableFields
            };
          }
          return platform;
        })
      );

      xsollaAnalytics.sendClick(`login_pa_module__platform-settings__disconnect`, projectId, {
        social: provider
      });

      setExistData(false);
      showSuccessNotify();
    } catch (err) {
      showErrorNotify(err);
    } finally {
      setFetching(false);
    }
  };

  return (
    <Modal onClose={onClose} size="md">
      <Form
        onSubmit={onSubmit}
        validate={platformSettingsValidator(provider)}
        initialValues={{
          ...platformData
        }}>
        {({ handleSubmit, errors, pristine }) => {
          const hasRequiredFields = !!currentPlatform?.configurable_fields.find((i) => i.required);

          return (
            <div>
              <PlatformHeaderWrapper>
                <PlatformHeader data-test-id="cross-modal-header">
                  {intl.messages[`auth-methods.${provider}`]}
                </PlatformHeader>
                {currentPlatform?.connected ? (
                  <Popper
                    entry={
                      // eslint-disable-next-line react/jsx-wrap-multilines
                      <List
                        items={[
                          {
                            children: intl.messages['social-connection.card.settings.disconnect'],
                            onClick: disconnectHandle
                          }
                        ]}
                      />
                    }>
                    {(referenceProps) => (
                      <PillAction
                        {...referenceProps}
                        palette="action"
                        data-test-id="cross-modal-status">
                        {intl.messages['social-connection.select.connected']}
                      </PillAction>
                    )}
                  </Popper>
                ) : (
                  <Pill palette="muted" data-test-id="cross-modal-status">
                    {intl.messages['social-connection.select.disconnected']}
                  </Pill>
                )}
              </PlatformHeaderWrapper>

              <PlatformFieldsWrapper>
                <FieldInput
                  name="client_id"
                  label={intl.formatMessage({
                    id: 'social-connection.settings.field.client_id.label'
                  })}
                  disabled={fetching}
                  errorMessage={
                    errors &&
                    errors.client_id &&
                    intl.formatMessage({
                      id: errors.client_id
                    })
                  }
                />
              </PlatformFieldsWrapper>

              <PlatformFieldsWrapper>
                <FieldInput
                  name="client_secret"
                  label={intl.formatMessage({
                    id: 'social-connection.settings.field.client_secret.label'
                  })}
                  disabled={fetching}
                  errorMessage={
                    errors &&
                    errors.client_secret &&
                    intl.formatMessage({
                      id: errors.client_secret
                    })
                  }
                />
              </PlatformFieldsWrapper>

              <FormFooter
                isModal
                onSubmit={handleSubmit}
                onCancel={onClose}
                fetching={fetching}
                submitText={intl.formatMessage({
                  id: `settings.platform-settings.${
                    currentPlatform?.connected ? 'update' : 'connect'
                  }`
                })}
                disabled={(hasRequiredFields && pristine) || fetching}
              />
            </div>
          );
        }}
      </Form>
    </Modal>
  );
};
